/**
 * Email template processing utilities.
 * Pure functions for variable substitution in subject/body; no API or UI.
 */

import type { Case } from '../types/case';
import type { User } from '../types/user';
import type { Client } from '../types';

/** Context objects for resolving merge fields when sending (e.g. {{case.number}}, {{user.firstName}}). */
export interface SystemContext {
  date?: string;
  time?: string;
  url?: string;
  companyName?: string;
}

export interface EmailTemplateContext {
  user?: User;
  client?: Client;
  case?: Case;
  system?: SystemContext;
}

/** Known merge keys aligned with availableMergeFields (e.g. case.number -> caseNumber). */
const MERGE_KEY_TO_RESOLVER: Record<string, (ctx: EmailTemplateContext) => string> = {
  'user.firstName': ctx => ctx.user?.firstName ?? '',
  'user.lastName': ctx => ctx.user?.lastName ?? '',
  'user.email': ctx => ctx.user?.email ?? '',
  'user.phone': ctx => ctx.user?.phoneNumber ?? '',
  'client.name': ctx => ctx.client?.name ?? '',
  'client.email': ctx => ctx.client?.email ?? '',
  'client.phone': ctx => ctx.client?.phone ?? '',
  'case.number': ctx => ctx.case?.caseNumber ?? '',
  'case.subject': ctx => ctx.case?.subject ?? '',
  'case.status': ctx => (ctx.case?.status as string) ?? '',
  'system.date': ctx => ctx.system?.date ?? new Date().toLocaleDateString(),
  'system.time': ctx => ctx.system?.time ?? new Date().toLocaleTimeString(),
  'system.url': ctx => ctx.system?.url ?? (typeof window !== 'undefined' ? window.location.origin : ''),
  'system.companyName': ctx => ctx.system?.companyName ?? 'Product Hub',
};

/**
 * Builds a flat variable map from context for processTemplate/processEmailTemplate.
 * Resolves dot-notation keys (e.g. case.number, user.firstName) from context objects.
 */
export function buildVariablesFromContext(context: EmailTemplateContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(MERGE_KEY_TO_RESOLVER)) {
    out[key] = MERGE_KEY_TO_RESOLVER[key](context);
  }
  return out;
}

/**
 * Replaces all {{key}} (and {{ key }}) placeholders in content with values from variables.
 * Missing keys are replaced with empty string.
 */
export function processTemplate(
  content: string,
  variables: Record<string, string>
): string {
  if (!content) return content;
  let result = content;
  // Match {{key}} or {{ key }} (optional whitespace inside braces)
  const placeholderRegex = /\{\{\s*([^}\s]+)\s*\}\}/g;
  result = result.replace(placeholderRegex, (_, key: string) => {
    const k = key.trim();
    return k in variables ? String(variables[k]) : '';
  });
  return result;
}

export interface TemplateContent {
  subject: string;
  body?: string;
  bodyHtml?: string;
}

export interface ProcessedEmail {
  subject: string;
  body: string;
}

/**
 * Processes subject and body/bodyHtml with the same variable map.
 * Uses bodyHtml if present, otherwise body; result body is a single string (HTML or plain).
 */
export function processEmailTemplate(
  template: TemplateContent,
  variables: Record<string, string>
): ProcessedEmail {
  const subject = processTemplate(template.subject, variables);
  const rawBody = template.bodyHtml ?? template.body ?? '';
  const body = processTemplate(rawBody, variables);
  return { subject, body };
}

/**
 * Extracts placeholder names from content (e.g. "{{name}}" -> "name", "{{ client.name }}" -> "client.name").
 */
export function extractVariableNames(content: string): string[] {
  if (!content) return [];
  const regex = /\{\{\s*([^}\s]+)\s*\}\}/g;
  const names = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    names.add(match[1].trim());
  }
  return Array.from(names);
}
